import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowDownTrayIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentArrowDownIcon,
  DocumentTextIcon,
  EyeIcon,
  FunnelIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { HsePageChrome } from "../../../../components/dashboard/hse/HsePageChrome";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../../components/utils/ResourceEmpty";
import { ModuleFilter } from "../../../../components/utils/ModuleFilter";
import { PMDialog } from "../../../../components/utils/PMDialog";
import {
  exportHseDocumentReadAcknowledgementsPdf,
  getHseDocumentReadAcknowledgements,
} from "../../../../api/hse/documents";
import { HseRecordActionsMenu } from "../../../../components/dashboard/hse/HseRecordActionsMenu";

const ackDay = (value) => {
  if (!value) return "";
  const iso = String(value);
  return iso.length >= 10 ? iso.slice(0, 10) : iso;
};

export const HseKsDocumentReadOverview = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [payload, setPayload] = useState(null);
  const [view, setView] = useState("employees");
  const [filters, setFilters] = useState({
    query: "",
    users: [],
    documents: [],
    readStatuses: [],
  });
  const [ackFrom, setAckFrom] = useState("");
  const [ackTo, setAckTo] = useState("");
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [expandedDocumentId, setExpandedDocumentId] = useState(null);
  const [printOpen, setPrintOpen] = useState(false);
  const [printScope, setPrintScope] = useState("pending");
  const [printDocumentId, setPrintDocumentId] = useState("");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError(false);
        const data = await getHseDocumentReadAcknowledgements();
        if (!cancelled) setPayload(data);
      } catch (err) {
        console.error("Error fetching HSE read acknowledgements", err);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const documentsById = useMemo(() => {
    const map = new Map();
    (payload?.documents || []).forEach((doc) => map.set(doc.id, doc));
    return map;
  }, [payload]);

  const employeesById = useMemo(() => {
    const map = new Map();
    (payload?.employees || []).forEach((emp) => map.set(emp.id, emp));
    (payload?.former_employees || []).forEach((emp) => map.set(emp.id, emp));
    return map;
  }, [payload]);

  const documentOptions = useMemo(
    () =>
      (payload?.documents || []).map((doc) => ({
        value: String(doc.id),
        label: doc.document_number ? `${doc.document_number} — ${doc.title}` : doc.title,
      })),
    [payload]
  );

  const userOptions = useMemo(
    () =>
      (payload?.employees || []).map((emp) => ({
        value: String(emp.id),
        label: emp.name || emp.email || String(emp.id),
      })),
    [payload]
  );

  const customDropdownFilters = useMemo(
    () => [
      {
        key: "documents",
        label: "hse.document_read_overview.filter_document",
        icon: DocumentTextIcon,
        options: documentOptions,
      },
      {
        key: "readStatuses",
        label: "hse.document_read_overview.filter_status",
        icon: FunnelIcon,
        options: [
          { value: "pending", label: t("hse.document_read_overview.filter_pending") },
          { value: "acknowledged", label: t("hse.document_read_overview.filter_acknowledged") },
          { value: "exempt", label: t("hse.document_read_overview.filter_exempt") },
        ],
      },
    ],
    [t, documentOptions]
  );

  const handleFiltersChange = useCallback((next) => {
    setFilters((prev) => {
      const query = next?.query || "";
      const users = next?.users || [];
      const documents = next?.documents || [];
      const readStatuses = next?.readStatuses || [];
      if (
        prev.query === query &&
        prev.users === users &&
        prev.documents === documents &&
        prev.readStatuses === readStatuses
      ) {
        return prev;
      }
      if (
        prev.query === query &&
        JSON.stringify(prev.users) === JSON.stringify(users) &&
        JSON.stringify(prev.documents) === JSON.stringify(documents) &&
        JSON.stringify(prev.readStatuses) === JSON.stringify(readStatuses)
      ) {
        return prev;
      }
      return { query, users, documents, readStatuses };
    });
  }, []);

  const filteredRows = useMemo(() => {
    const q = (filters.query || "").trim().toLowerCase();
    const userIds = new Set((filters.users || []).map(String));
    const docIds = new Set((filters.documents || []).map(String));
    const statuses = new Set(filters.readStatuses || []);

    return (payload?.rows || []).filter((row) => {
      if (userIds.size && !userIds.has(String(row.user_id))) return false;
      if (docIds.size && !docIds.has(String(row.document_id))) return false;
      if (statuses.size && !statuses.has(row.status)) return false;
      if (ackFrom || ackTo) {
        const day = ackDay(row.read_understood_at);
        if (!day) return false;
        if (ackFrom && day < ackFrom) return false;
        if (ackTo && day > ackTo) return false;
      }
      if (!q) return true;
      const emp = employeesById.get(row.user_id);
      const doc = documentsById.get(row.document_id);
      const haystack = [emp?.name, emp?.email, doc?.title, doc?.document_number]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [payload, filters, ackFrom, ackTo, employeesById, documentsById]);

  const groupRowsByEmployee = useCallback(
    (rows) => {
      const groups = new Map();
      rows.forEach((row) => {
        if (!groups.has(row.user_id)) {
          groups.set(row.user_id, {
            employee: employeesById.get(row.user_id),
            rows: [],
          });
        }
        groups.get(row.user_id).rows.push(row);
      });
      return Array.from(groups.values()).sort((a, b) =>
        (a.employee?.name || a.employee?.email || "").localeCompare(
          b.employee?.name || b.employee?.email || "",
          "nb"
        )
      );
    },
    [employeesById]
  );

  const employeeGroups = useMemo(
    () => groupRowsByEmployee(filteredRows),
    [filteredRows, groupRowsByEmployee]
  );

  const documentGroups = useMemo(() => {
    const groups = new Map();
    filteredRows.forEach((row) => {
      if (!groups.has(row.document_id)) {
        groups.set(row.document_id, {
          document: documentsById.get(row.document_id),
          rows: [],
        });
      }
      groups.get(row.document_id).rows.push(row);
    });
    return Array.from(groups.values()).sort((a, b) =>
      (a.document?.title || "").localeCompare(b.document?.title || "", "nb")
    );
  }, [filteredRows, documentsById]);

  const statusLabel = (status) => {
    if (status === "acknowledged") return t("hse.document_read_overview.status_acknowledged");
    if (status === "exempt") return t("hse.document_read_overview.status_exempt");
    return t("hse.document_read_overview.status_pending");
  };

  const statusClass = (status) => {
    if (status === "acknowledged") return "bg-emerald-50 text-emerald-800 ring-emerald-200";
    if (status === "exempt") return "bg-slate-100 text-slate-600 ring-slate-200";
    return "bg-amber-50 text-amber-800 ring-amber-200";
  };

  const formatWhen = (value) => {
    if (!value) return "—";
    return new Date(value).toLocaleDateString();
  };

  const openPrintDialog = () => {
    const selectedStatuses = filters.readStatuses || [];
    if (selectedStatuses.length === 1 && selectedStatuses[0] === "acknowledged") {
      setPrintScope("acknowledged");
    } else if (selectedStatuses.length === 1 && selectedStatuses[0] === "pending") {
      setPrintScope("pending");
    } else {
      setPrintScope("pending");
    }
    setPrintDocumentId(filters.documents?.length === 1 ? String(filters.documents[0]) : "");
    setPrintOpen(true);
  };

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await exportHseDocumentReadAcknowledgementsPdf({
        scope: printScope,
        document_id: printDocumentId || undefined,
        locale: i18n.language,
      });
      setPrintOpen(false);
      toast.success(t("hse.actions.export_pdf_success"));
    } catch (err) {
      console.error("Error exporting HSE read acknowledgements PDF", err);
      toast.error(t("hse.document_read_overview.export_error"));
    } finally {
      setExporting(false);
    }
  };

  const exportReadPdf = async ({ documentId, userId } = {}) => {
    if (exporting) return;
    setExporting(true);
    try {
      await exportHseDocumentReadAcknowledgementsPdf({
        scope: "all",
        document_id: documentId,
        user_id: userId,
        locale: i18n.language,
      });
      toast.success(t("hse.actions.export_pdf_success"));
    } catch (err) {
      console.error("Error exporting HSE read acknowledgements PDF", err);
      toast.error(t("hse.document_read_overview.export_error"));
    } finally {
      setExporting(false);
    }
  };

  const summary = payload?.summary || {};

  return (
    <HsePageChrome
      title={t("hse.document_read_overview.title")}
      subtitle={t("hse.document_read_overview.subtitle")}
      actions={[
        {
          label: t("hse.document_read_overview.print_action"),
          action: openPrintDialog,
          icon: ArrowDownTrayIcon,
          hidden: loading || error || !payload,
          menu: false,
        },
        {
          label: t("hse.document_read_overview.back_to_documents"),
          action: () => navigate("/dashboard/hse-ks/documents"),
          hidden: false,
          menu: false,
        },
      ]}
    >
      <div className="mx-auto mt-8 max-w-7xl px-4 pb-10 sm:px-6">
        {loading ? (
          <ResourceLoading />
        ) : error ? (
          <ResourceError backTo="/dashboard/hse-ks/documents" />
        ) : !payload ? (
          <ResourceError variant="not_found" backTo="/dashboard/hse-ks/documents" />
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
              <SummaryCard
                label={t("hse.document_read_overview.summary_pending")}
                value={summary.pending_count || 0}
                tone="amber"
              />
              <SummaryCard
                label={t("hseCompliance.overview.summary_overdue")}
                value={summary.overdue_count || 0}
                tone={summary.overdue_count ? "red" : undefined}
              />
              <SummaryCard
                label={t("hse.document_read_overview.summary_acknowledged")}
                value={summary.acknowledged_count || 0}
                tone="green"
              />
              <SummaryCard
                label={t("hse.document_read_overview.summary_documents")}
                value={summary.document_count || 0}
              />
              <SummaryCard
                label={t("hse.document_read_overview.summary_employees")}
                value={summary.employee_count || 0}
              />
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <ModuleFilter
                filterCard
                hideStatusFilter
                searchPlaceholder="hse.document_read_overview.search_placeholder"
                usersFilterLabel={t("hse.document_read_overview.filter_employee")}
                userOptions={userOptions}
                customDropdownFilters={customDropdownFilters}
                initialFilters={filters}
                onFiltersChange={handleFiltersChange}
                segmentedControl={{
                  value: view,
                  onChange: setView,
                  options: [
                    { value: "employees", label: t("hse.document_read_overview.view_employees") },
                    { value: "documents", label: t("hse.document_read_overview.view_documents") },
                  ],
                }}
              />
              <div className="mt-3 flex flex-wrap items-center gap-2.5">
                <label className="inline-flex h-[46px] items-center gap-2 rounded-full border border-slate-200 bg-white px-[18px] text-[13.5px] font-semibold text-slate-800">
                  <span>{t("hse.document_read_overview.filter_acknowledged_from")}</span>
                  <input
                    type="date"
                    value={ackFrom}
                    onChange={(e) => setAckFrom(e.target.value)}
                    className="border-none bg-transparent text-sm font-medium text-slate-900 outline-none"
                  />
                </label>
                <label className="inline-flex h-[46px] items-center gap-2 rounded-full border border-slate-200 bg-white px-[18px] text-[13.5px] font-semibold text-slate-800">
                  <span>{t("hse.document_read_overview.filter_acknowledged_to")}</span>
                  <input
                    type="date"
                    value={ackTo}
                    onChange={(e) => setAckTo(e.target.value)}
                    className="border-none bg-transparent text-sm font-medium text-slate-900 outline-none"
                  />
                </label>
              </div>
            </div>

            {filteredRows.length === 0 ? (
              <ResourceEmpty text={t("hse.document_read_overview.empty")} />
            ) : view === "employees" ? (
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                        {t("hse.document_read_overview.col_employee")}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                        {t("hse.document_read_overview.col_pending")}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                        {t("hse.document_read_overview.col_acknowledged")}
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                        {t("hse.actions.menu")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {employeeGroups.map(({ employee, rows }) => {
                      const pendingDocs = rows.filter((row) => row.status === "pending");
                      const userId = employee?.id || rows[0]?.user_id;
                      const open = expandedUserId === userId;
                      const isFormer = employee?.current === false;
                      return (
                        <React.Fragment key={userId}>
                          <tr
                            className="cursor-pointer hover:bg-slate-50"
                            onClick={() =>
                              setExpandedUserId(open ? null : userId)
                            }
                          >
                            <td className="px-4 py-3">
                              <div className="font-medium text-slate-900">
                                {employee?.name || employee?.email || t("hse.document_read_overview.unknown_user")}
                                {isFormer && (
                                  <span className="ml-2 inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                                    {t("hse.document_read_overview.former_employee")}
                                  </span>
                                )}
                              </div>
                              {employee?.email && employee?.name && (
                                <div className="text-sm text-slate-500">{employee.email}</div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm font-semibold text-amber-800">
                              {pendingDocs.length}
                              {pendingDocs.some((row) => row.overdue) ? (
                                <span className="pm-badge pm-badge--bad ml-2">
                                  {t("hseCompliance.overview.overdue_count", { count: pendingDocs.filter((row) => row.overdue).length })}
                                </span>
                              ) : null}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-700">
                              {rows.filter((row) => row.status === "acknowledged").length}
                            </td>
                            <td className="px-4 py-3 text-right" onClick={(event) => event.stopPropagation()}>
                              <HseRecordActionsMenu
                                items={[
                                  {
                                    key: "routines",
                                    label: t("hseCompliance.overview.open_routines"),
                                    icon: EyeIcon,
                                    onClick: () => navigate(`/dashboard/hse-ks/documents/employees/${employee?.id || userId}`),
                                  },
                                  {
                                    key: "export_pdf",
                                    label: t("hse.actions.export_pdf"),
                                    icon: DocumentArrowDownIcon,
                                    disabled: exporting,
                                    onClick: () => exportReadPdf({ userId: employee?.id }),
                                  },
                                ]}
                              />
                            </td>
                          </tr>
                          {open && (
                            <tr>
                              <td colSpan={4} className="bg-slate-50 px-4 py-3">
                                <ul className="space-y-2">
                                  {rows.map((row) => {
                                    const doc = documentsById.get(row.document_id);
                                    return (
                                      <li
                                        key={`${row.document_id}-${row.user_id}`}
                                        className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white px-3 py-2 ring-1 ring-slate-200"
                                      >
                                        <Link
                                          to={`/dashboard/hse-ks/documents/${row.document_id}`}
                                          className="text-sm font-medium text-[#1463C7] hover:underline"
                                        >
                                          {doc?.document_number ? `${doc.document_number} — ` : ""}
                                          {doc?.title || t("hse.document_read_overview.unknown_document")}
                                        </Link>
                                        <div className="flex items-center gap-2">
                                          <span
                                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ${statusClass(row.status)}`}
                                          >
                                            {row.status === "pending" ? (
                                              <ClockIcon className="mr-1 h-3.5 w-3.5" />
                                            ) : (
                                              <CheckCircleIcon className="mr-1 h-3.5 w-3.5" />
                                            )}
                                            {statusLabel(row.status)}
                                            {row.read_understood_at
                                              ? ` · ${formatWhen(row.read_understood_at)}`
                                              : ""}
                                          </span>
                                          <HseRecordActionsMenu
                                            items={[
                                              {
                                                key: "open",
                                                label: t("hse.actions.open"),
                                                icon: EyeIcon,
                                                onClick: () =>
                                                  navigate(`/dashboard/hse-ks/documents/${row.document_id}`),
                                              },
                                              {
                                                key: "export_pdf",
                                                label: t("hse.actions.export_pdf"),
                                                icon: DocumentArrowDownIcon,
                                                disabled: exporting,
                                                onClick: () => exportReadPdf({ documentId: row.document_id }),
                                              },
                                            ]}
                                          />
                                        </div>
                                      </li>
                                    );
                                  })}
                                </ul>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                        {t("hse.document_read_overview.col_document")}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                        {t("hse.document_read_overview.col_pending")}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                        {t("hse.document_read_overview.col_acknowledged")}
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                        {t("hse.actions.menu")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {documentGroups.map(({ document, rows }) => {
                      const pendingPeople = rows.filter((row) => row.status === "pending");
                      const open = expandedDocumentId === document?.id;
                      return (
                        <React.Fragment key={document?.id || rows[0]?.document_id}>
                          <tr
                            className="cursor-pointer hover:bg-slate-50"
                            onClick={() =>
                              setExpandedDocumentId(open ? null : document?.id)
                            }
                          >
                            <td className="px-4 py-3">
                              <div className="font-medium text-slate-900">{document?.title}</div>
                              {document?.document_number && (
                                <div className="text-sm text-slate-500">{document.document_number}</div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm font-semibold text-amber-800">
                              {pendingPeople.length}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-700">
                              {rows.filter((row) => row.status === "acknowledged").length}
                            </td>
                            <td className="px-4 py-3 text-right" onClick={(event) => event.stopPropagation()}>
                              <HseRecordActionsMenu
                                items={[
                                  {
                                    key: "open",
                                    label: t("hse.actions.open"),
                                    icon: EyeIcon,
                                    hidden: !document?.id,
                                    onClick: () =>
                                      navigate(`/dashboard/hse-ks/documents/${document.id}`),
                                  },
                                  {
                                    key: "export_pdf",
                                    label: t("hse.actions.export_pdf"),
                                    icon: DocumentArrowDownIcon,
                                    disabled: exporting || !document?.id,
                                    onClick: () => exportReadPdf({ documentId: document.id }),
                                  },
                                ]}
                              />
                            </td>
                          </tr>
                          {open && (
                            <tr>
                              <td colSpan={4} className="bg-slate-50 px-4 py-3">
                                <ul className="space-y-2">
                                  {rows.map((row) => {
                                    const emp = employeesById.get(row.user_id);
                                    return (
                                      <li
                                        key={`${row.document_id}-${row.user_id}`}
                                        className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white px-3 py-2 ring-1 ring-slate-200"
                                      >
                                        <span className="text-sm text-slate-800">
                                          {emp?.name || emp?.email || t("hse.document_read_overview.unknown_user")}
                                          {emp?.current === false && (
                                            <span className="ml-2 inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                                              {t("hse.document_read_overview.former_employee")}
                                            </span>
                                          )}
                                        </span>
                                        <span
                                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ${statusClass(row.status)}`}
                                        >
                                          {statusLabel(row.status)}
                                          {row.read_understood_at
                                            ? ` · ${formatWhen(row.read_understood_at)}`
                                            : ""}
                                        </span>
                                      </li>
                                    );
                                  })}
                                </ul>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      <PMDialog
        open={printOpen}
        onClose={() => {
          if (!exporting) setPrintOpen(false);
        }}
        closeOnOutsideClick={!exporting}
        title="hse.document_read_overview.print_dialog_title"
        size="lg"
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600">{t("hse.document_read_overview.print_dialog_intro")}</p>
          <fieldset>
            <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t("hse.document_read_overview.print_scope")}
            </legend>
            <div className="flex flex-col gap-2">
              {[
                ["pending", t("hse.document_read_overview.print_scope_pending")],
                ["acknowledged", t("hse.document_read_overview.print_scope_acknowledged")],
                ["all", t("hse.document_read_overview.print_scope_all")],
              ].map(([value, label]) => (
                <label key={value} className="inline-flex items-center gap-2 text-sm text-slate-800">
                  <input
                    type="radio"
                    name="hse-read-print-scope"
                    value={value}
                    checked={printScope === value}
                    onChange={() => setPrintScope(value)}
                  />
                  {label}
                </label>
              ))}
            </div>
          </fieldset>
          {documentOptions.length > 0 ? (
            <label className="block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                {t("hse.document_read_overview.filter_document")}
              </span>
              <select
                value={printDocumentId}
                onChange={(e) => setPrintDocumentId(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900"
              >
                <option value="">{t("hse.document_read_overview.all_documents")}</option>
                {documentOptions.map((doc) => (
                  <option key={doc.value} value={doc.value}>
                    {doc.label}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setPrintOpen(false)}
              disabled={exporting}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            >
              {t("hse.document_read_overview.print_cancel")}
            </button>
            <button
              type="button"
              onClick={handleExport}
              disabled={exporting}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {exporting
                ? t("hse.document_read_overview.export_loading")
                : t("hse.document_read_overview.print_submit")}
            </button>
          </div>
        </div>
      </PMDialog>
    </HsePageChrome>
  );
};

const SummaryCard = ({ label, value, tone }) => {
  const toneClass =
    tone === "red"
      ? "border-red-200 bg-red-50"
      : tone === "amber"
      ? "border-amber-200 bg-amber-50"
      : tone === "green"
        ? "border-emerald-200 bg-emerald-50"
        : "border-slate-200 bg-white";
  return (
    <div className={`rounded-xl border px-4 py-3 ${toneClass}`}>
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-bold text-slate-900">{value}</div>
    </div>
  );
};
